<template>
  <div class="container-nzidoo">

    <page-title title="sentence.myFavorites" />

    <!-- Tabs -->
    <div class="tabs">
      <button class="btn-tab" :class="{ active: activeTab === 'products' }" @click="activeTab = 'products'">
        {{ $t('sentence.favoriteProducts') }}
      </button>
      <button class="btn-tab" :class="{ active: activeTab === 'sellers' }" @click="activeTab = 'sellers'">
        {{ $t('sentence.favoriteSellers') }}
      </button>
    </div>

    <!-- Search Input -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>
        <input type="text" v-model="searchTerm"
          :placeholder="activeTab === 'products' ? $t('sentence.searchByName') : $t('sentence.searchByName')"
          class="admin-search-input">
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
    </div>

    <loading-spinner v-if="loading" />

    <!-- Products Tab -->
    <div v-else-if="activeTab === 'products'">
      <!-- No favorites -->
      <div v-if="favorites.length === 0" class="text-center text-muted py-5">
        <i class="mdi mdi-heart-outline fa-3x mb-3"></i>
        <p class="px-3">{{ $t('sentence.noFavoritesYet') }}</p>
      </div>

      <!-- No search results -->
      <div v-else-if="filteredFavorites.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
      </div>

      <!-- Favorites Grid -->
      <div v-else class="">
        <div class="row px-2 px-md-3 g-4">
          <div v-for="fav in filteredFavorites" :key="fav.id" class="col-md py-1 px-0 px-md-1 favorites-list">

            <div class="favorite-content" @click="moveToProductPage(fav.product.id)">
              <ProductCard v-if="fav && fav.product" :pictures="fav.product?.pictures || []" :user="fav?.user || {}"
                :product="fav.product" :sales_type="fav.product?.sales_type || 'fixed'" :storageUrl="storageUrl"
                :title="fav.product?.name || ''" :price="fav.product?.fixed_price || 0"
                :isValidated="fav.product?.validated || null" :startingPrice="fav.product?.starting_price || 0"
                :showBtn="false" />

            </div>

            <!-- Remove button -->
            <button class="btn btn-sm btn-remove" @click.stop="removeFavorite(fav.id)" :title="$t('action.delete')">
              <i class="mdi mdi-trash-can"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sellers Tab -->
    <div v-else-if="activeTab === 'sellers'">
      <!-- No favorite sellers -->
      <div v-if="favoriteSellers.length === 0" class="text-center text-muted py-5">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
      </div>

      <!-- No search results -->
      <div v-else-if="filteredSellers.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
      </div>

      <!-- Sellers Grid -->
      <div v-else class="row px-2 px-md-3 g-4">
        <div v-for="seller in filteredSellers" :key="seller.id" class="col-md-4 py-1 px-0 px-md-1">
          <div class="seller-card" @click="moveToUserPage(seller.seller.id)">
            <div class="seller-avatar">
              <img v-if="seller.seller?.picture" :src="`${storageUrl}/${seller.seller.picture}`"
                :alt="seller.seller?.username || 'Seller'" />
              <i v-else class="mdi mdi-account-circle"></i>
            </div>
            <div class="seller-info">
              <h5 class="seller-name">{{ seller.seller?.username || 'Unknown Seller' }}</h5>
              <!-- <p class="seller-email">{{ seller.seller?.created_at | moment }}</p> -->
            </div>
            <button class="btn btn-sm btn-remove" @click.stop="removeFavoriteSeller(seller.id)"
              :title="$t('action.delete')">
              <i class="mdi mdi-trash-can"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductCard from "@/components/templates/ProductCard.vue";
import config from "@/config.js";
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from '@/components/templates/LoadingSpinner.vue';

export default {
  name: "ManageFavorites",
  components: {
    ProductCard,
    PageTitle,
    LoadingSpinner,
  },
  data() {
    return {
      activeTab: 'sellers',
      favorites: [],
      favoriteSellers: [],
      loading: true,
      storageUrl: config.storageUrl,
      searchTerm: '',
    };
  },
  computed: {
    filteredFavorites() {
      if (!this.searchTerm.trim()) {
        return this.favorites;
      }

      const query=this.searchTerm.toLowerCase().trim();
      return this.favorites.filter(fav => {
        const productName=fav.product?.name?.toLowerCase()||'';
        const productPrice=fav.product?.price?.toLowerCase()||'';

        return productName.includes(query)||
          productPrice.includes(query);
      });
    },
    filteredSellers() {
      if (!this.searchTerm.trim()) {
        return this.favoriteSellers;
      }

      const query=this.searchTerm.toLowerCase().trim();
      return this.favoriteSellers.filter(seller => {
        const sellerName=seller.seller?.name?.toLowerCase()||'';
        const sellerEmail=seller.seller?.email?.toLowerCase()||'';

        return sellerName.includes(query)||sellerEmail.includes(query);
      });
    }
  },
  watch: {
    activeTab() {
      this.searchTerm='';
    }
  },
  async mounted() {
    await this.loadFavorites();
  },
  methods: {
    async loadFavorites() {
      try {
        const user=this.$store?.state?.user;
        if (!user) {
          this.$router.push("/login");
          return;
        }

        // Load favorite products
        const res=await this.$axios.get("/favorites");

        this.favorites=res.data.data.favorites||[];
        this.favoriteSellers=res.data.data.sellerfavorites||[];

      } catch (error) {
        console.error("Failed to load favorites:", error);
        this.$toast?.error("Failed to load favorites");
      } finally {
        this.loading=false;
      }
    },

    moveToProductPage(id) {
      this.$router.push({ name: "productDetails", params: { id } });
    },

    moveToUserPage(id) {
      this.$router.push({ name: "Profile", params: { id } });
    },

    async removeFavorite(favoriteId) {
      try {
        // Optimistically update UI
        this.favorites=this.favorites.filter(f => f.id!==favoriteId);

        // Call API to remove by favorite ID
        await this.$axios.delete(`/favorites/${favoriteId}`);

        this.$toast?.success("Removed from favorites");
      } catch (error) {
        console.error("Failed to remove favorite:", error);
        this.$toast?.error("Failed to remove from favorites");
        // Reload on error
        await this.loadFavorites();
      }
    },

    async removeFavoriteSeller(sellerId) {
      try {
        // Optimistically update UI
        this.favoriteSellers=this.favoriteSellers.filter(s => s.id!==sellerId);

        // Call API to remove by seller ID
        await this.$axios.delete(`/favorite-sellers/${sellerId}`);

        this.$toast?.success("Removed from favorite sellers");
      } catch (error) {
        console.error("Failed to remove favorite seller:", error);
        this.$toast?.error("Failed to remove from favorite sellers");
        // Reload on error
        await this.loadFavorites();
      }
    },

    clearSearch() {
      this.searchTerm='';
    }
  }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>
/* Existing Styles */
.favorites-list {
  display: flex;
  flex: 1 1 30%;
  align-items: center;
  border-radius: 12px;
  background: white;
  margin-inline-end: 0.5rem;
  margin-block-end: 0.5rem;
}

.favorite-content {
  flex: 1;
  cursor: pointer;
  min-width: 0;
}

.mdi {
  font-size: 20px;
}

.product-card:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Seller Card Styles */
.seller-card {
  display: flex;
  align-items: center;
  padding: 15px;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.seller-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.seller-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--light-gray);
}

.seller-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.seller-avatar i {
  font-size: 40px;
  color: var(--gray);
}

.seller-info {
  flex: 1;
  min-width: 0;
}

.seller-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.seller-email {
  font-size: 13px;
  color: var(--gray);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}



/* Tablet and below */
@media (max-width: 768px) {
  .container-nzidoo {
    padding-left: 10px;
    padding-right: 10px;
  }

  .mdi {
    font-size: 18px;
  }

  .seller-avatar {
    width: 50px;
    height: 50px;
  }

  .seller-avatar i {
    font-size: 35px;
  }

  .seller-name {
    font-size: 15px;
  }

  .seller-email {
    font-size: 12px;
  }
}

/* Mobile devices */
@media (max-width: 576px) {
  .container-nzidoo {
    padding-left: 8px;
    padding-right: 8px;
  }

  .favorites-list {
    gap: 0.5rem;
  }

  .mdi {
    font-size: 16px;
  }

  .seller-card {
    padding: 12px;
  }

  .seller-avatar {
    width: 45px;
    height: 45px;
    margin-right: 12px;
  }

  .seller-avatar i {
    font-size: 30px;
  }

  .seller-name {
    font-size: 14px;
  }

  .seller-email {
    font-size: 11px;
  }
}
</style>